import { Button, Skeleton, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { TbUserPlus } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_ROLE_LABEL } from '../Contracts/DiskShare'
import { personName } from '../Helpers/diskFormat'
import { DiskPrincipalAvatar } from './DiskPrincipal'

import type { DiskNodeShares, DiskPrincipalType } from '../Contracts/DiskShare'
import type { DiskUserRef } from '../Contracts/DiskNode'

import styles from './DiskShare.module.css'

const { Text } = Typography

/** Скільки рядків показати в дровері; решта — «ще N» і модалка. */
const ACCESS_PREVIEW = 5

interface AccessLine {
    key: string
    type: DiskPrincipalType
    user: DiskUserRef | null
    title: string
    role: string
    note: string
}

/** Учасники простору, успадковані й надані тут — одним переліком, надані тут першими. */
const accessLines = (data: DiskNodeShares): AccessLine[] => [
    ...data.direct.map((row) => ({
        key: row.uuid,
        type: row.principalType,
        user: row.principalUser,
        title: row.principalTitle,
        role: row.roleLabel,
        note: 'надано тут',
    })),
    ...data.inherited.map((row) => ({
        key: row.uuid,
        type: row.principalType,
        user: row.principalUser,
        title: row.principalTitle,
        role: row.roleLabel,
        note: row.fromNode ? `від «${row.fromNode.name}»` : 'від папки вище',
    })),
    ...(data.owner
        ? [
              {
                  key: 'owner',
                  type: 'user' as const,
                  user: data.owner,
                  title: personName(data.owner),
                  role: DISK_ROLE_LABEL.manager,
                  note: 'власник',
              },
          ]
        : []),
    ...data.storageMembers.map((row) => ({
        key: row.uuid,
        type: row.principalType,
        user: row.principalUser,
        title: row.principalTitle,
        role: row.roleLabel,
        note: 'учасник простору',
    })),
]

/**
 * Секція «Доступ» дровера «Властивості» (`DISK-005` §3.6.1): моя роль і
 * коротко, хто ще має доступ; «Керувати доступом» відкриває модалку
 * «Поділитися» (без права ділитися — «Хто має доступ», лише перегляд).
 */
export const DiskAccessSection = observer(
    ({ access, error, onManage }: { access: DiskNodeShares | null; error: string | null; onManage: () => void }) => {
        if (error !== null) return <Text type="secondary">{error}</Text>
        if (access === null) return <Skeleton active paragraph={{ rows: 2 }} title={false} />

        const lines = accessLines(access)
        const shown = lines.slice(0, ACCESS_PREVIEW)
        const canManage = access.me.canShare && !access.node.root

        return (
            <div className={classNames(styles['access'])} data-testid="disk-properties-access">
                {access.me.role !== null && (
                    <Text type="secondary">
                        Ваш доступ: <Text strong>{access.me.roleLabel}</Text>
                        {access.me.source ? ` — ${access.me.source.title}` : ''}
                    </Text>
                )}
                <div className={classNames(styles['accessList'])}>
                    {shown.map((line) => (
                        <div key={line.key} className={classNames(styles['accessItem'])}>
                            <span className={classNames(styles['option'])}>
                                <DiskPrincipalAvatar type={line.type} user={line.user} title={line.title} size={24} />
                                <span className={classNames(styles['optionText'])}>
                                    <span className={classNames(styles['whoTitle'])}>{line.title}</span>
                                    <span className={classNames(styles['whoSub'])}>{line.note}</span>
                                </span>
                            </span>
                            <span className={classNames(styles['roleText'])}>{line.role}</span>
                        </div>
                    ))}
                    {lines.length > shown.length && <Text type="secondary">і ще {lines.length - shown.length}</Text>}
                </div>
                <Button icon={<TbUserPlus />} onClick={onManage} data-testid="disk-properties-manage-access">
                    {canManage ? 'Керувати доступом' : 'Хто має доступ'}
                </Button>
            </div>
        )
    }
)
